/**
 * PHI overlay -- the split read (TEST_INTEGRATION_PLAN.md §28) and, since
 * the independence switch (§31), the write forward too.
 *
 * The target architecture: Supabase keeps the NON-PHI part of every row
 * (ids, user_id / shared_users, record timestamps, flags, enums) and RDS
 * holds the PHI columns. A read is therefore a JOIN: the app's Supabase
 * query runs exactly as today and returns its rows; for every PHI table
 * the rows' PHI columns are then replaced by RDS's values for the same
 * ids, fetched with the user's own session (POST /phi/{schema}/{table}/list,
 * which returns each row RAW when rds_access.resolve() entitles the viewer
 * and MASKED otherwise).
 *
 * This is installed ONCE, as a wrapper on the Supabase client that
 * components/supabaseClient.js hands out, so every `.from(<PHI table>)`
 * read in the app is covered without touching its call site -- the 129
 * reads §27 counted.
 *
 * Rules, per row:
 *   - only columns the Supabase select RETURNED are overlaid (the caller's
 *     shape is preserved), and only PHI ones: NON_PHI_COLUMNS is the same
 *     set RDS's _mask_row keeps, plus `id`; a result that returned no PHI
 *     column at all is handed back without asking RDS;
 *   - a row RDS returns RAW gets RDS's values; a row RDS MASKS
 *     (access_level 'none') keeps Supabase's values for now -- Supabase's
 *     RLS already showed them, and redacting on screen what the user could
 *     see a minute ago is a policy change to make deliberately (Phase G),
 *     not a side effect of the overlay; it is COUNTED (`masked`) so the
 *     dashboard shows where RDS is stricter than Supabase;
 *   - a row RDS does not have (`missing`) keeps Supabase's values;
 *   - any RDS failure leaves the whole result untouched and is reported.
 *
 * One flag per table (`overlay_<table>`), effective only while that
 * table's mirror is fresh; an RDS_API_URL on the backend
 * gates everything, exactly as for the per-site cutovers. Off = the client
 * is returned unwrapped, byte-for-byte today's behaviour.
 *
 * WRITES (plan §31, the write overlay). While RDS is independent the
 * whole-table mirror has stopped, so a row the app writes to Supabase
 * would never reach RDS -- and the read path above would then overlay
 * stale PHI onto it. Every mutating chain (.insert/.update/.upsert/
 * .delete) on a PHI table therefore still goes to Supabase FIRST, exactly
 * as today, and once Supabase has accepted it the same change is
 * forwarded to RDS with the user's own session (POST .../write with `op`,
 * POST .../delete), awaited before the caller sees the result so a
 * follow-up read cannot race its own write. Active when the table's
 * overlay flag is effective OR RDS is independent (the sticky mode, so an
 * unreachable flag lookup never silences the forward -- data #3).
 *
 * Forward rules, per statement:
 *   - ids come from the chain (`.eq("id")`, `.in("id")`, `.match({id})`)
 *     or, when the chain gives none (inserts, updates/deletes keyed by
 *     another column), from a `select()` auto-chained on the builder so
 *     Supabase RETURNS the rows; the caller's shape is then RESTORED to
 *     `data: null` (what a chain without select returned), while `status`
 *     / `statusText` are whatever the representation request answered
 *     (data #15);
 *   - updates forward only what changed (`{id, ...payload}`, op update);
 *     when RDS lacks the row (`written:false, reason:"missing"`) the full
 *     row is re-read from Supabase and upserted instead, so a partial row
 *     never overlays NULL PHI (a re-read that fails is retried like any
 *     other transient failure); a payload with neither a PHI key nor an
 *     owner/share key is not forwarded at all (nothing RDS holds changed);
 *   - inserts/upserts forward the rows Supabase RETURNED as full upserts,
 *     so those must be whole: when the caller chained a column-limited
 *     select (`.select("id")`) the full rows are re-read through the raw
 *     client first (falling back to payload + returned row), so an RDS
 *     row is never overwritten with NULL PHI for the columns not selected;
 *   - forwards for one (schema, table, id) are serialized so two edits of
 *     the same row land in order; distinct ids run at most 10 in flight;
 *   - a failed forward (except 403 and "no primary key", which no retry
 *     can fix) is retried from an in-memory, viewer-bound queue at 1 s,
 *     5 s and 30 s; a later update of the same row by the same viewer
 *     CARRIES the queued update's columns (queued first, newer wins) and
 *     drops the queue entry once it lands; a later forward of another op
 *     supersedes the queued one, which is then reported lost -- never
 *     dropped silently; when the last retry fails the loss is reported to
 *     the dashboard (`rds_write_lost`) AND recorded durably on RDS (POST
 *     /audit/write-miss; clinical #11). The queue is never persisted and
 *     an entry is dropped when the signed-in user changed;
 *   - one report per statement under `write_overlay_<table>` with `op`;
 *     counts, column names and error classes only -- never row content.
 *
 * No top-level import of rdsPhi / rdsCutover: supabaseClient.js imports
 * this module, and those two import supabaseClient.js -- the requires are
 * deferred to call time to keep the module graph acyclic.
 */

// Tables RDS mirrors (mirror_sync.DEFAULT_TABLES) -- the only ones overlaid.
export const PHI_TABLES = new Set([
  "notes-c", "patients", "patients_with_ehrid", "patients_no_ehrid", "SMS",
]);

// Verbatim from RDS/main.py _NON_PHI_COLUMNS (lower-cased there too): keys,
// ownership, record timestamps, flags/enums. These are Supabase's and are
// never overwritten by the overlay.
export const NON_PHI_COLUMNS = new Set([
  "id", "user_id", "created_by", "owner_id", "shared_users", "shared_with", "schema",
  "created_at", "updated_at", "last_updated", "synced_at",
  "confirmation_updated_at", "confirmation_sent_at",
  "type", "format", "slot_status", "forms_ind", "timezone", "template",
  "preferred_language", "confirmation_status", "confirmation_source",
  "confirmation_updated_by", "confirmation_rules_sent", "sex_code",
  "current_gender", "content_type", "size", "status", "kind", "version",
  "access_level",
]);

const RAW_ACCESS = new Set(["owner", "view", "write", "admin", "superadmin"]);
const MUTATING = new Set(["insert", "update", "upsert", "delete"]);
const CHUNK = 200; // RDS /list caps at 1000; 200 keeps each request small.
// Chunks of one read in flight at once: the browser allows six connections
// to RDS, and other reads need some of them too.
const CHUNK_CONCURRENCY = 3;

/** The cutover flag / dashboard pilot for a PHI table's overlay. */
export function overlayFlagFor(table) {
  return `overlay_${String(table).replace(/-/g, "_").toLowerCase()}`;
}

function isPhiKey(k) {
  return !NON_PHI_COLUMNS.has(String(k).toLowerCase()) && !String(k).startsWith("_");
}

function same(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Merge RDS's rows into Supabase's, keyed by String(id). Pure; exported
 * for tests. Returns { rows, matched, mismatched, missing, masked, columns }.
 */
export function mergePhi(supabaseRows, rdsRows) {
  const byId = new Map();
  (rdsRows || []).forEach((r) => {
    if (r && r.id !== undefined && r.id !== null) byId.set(String(r.id), r);
  });
  let matched = 0;
  let mismatched = 0;
  let missing = 0;
  let masked = 0;
  const columns = new Set();
  const rows = supabaseRows.map((sb) => {
    if (!sb || sb.id === undefined || sb.id === null) return sb;
    const rds = byId.get(String(sb.id));
    if (!rds) { missing += 1; return sb; }
    if (!RAW_ACCESS.has(rds.access_level)) { masked += 1; return sb; }
    const out = { ...sb };
    let differed = false;
    Object.keys(sb).forEach((k) => {
      if (!isPhiKey(k) || !Object.prototype.hasOwnProperty.call(rds, k)) return;
      columns.add(k);
      if (!same(sb[k], rds[k])) differed = true;
      out[k] = rds[k];
    });
    if (differed) mismatched += 1; else matched += 1;
    return out;
  });
  return { rows, matched, mismatched, missing, masked, columns: Array.from(columns).sort() };
}

async function fetchRdsRows(schema, table, ids) {
  // eslint-disable-next-line global-require
  const { listRowsByIds } = require("./rdsPhi");
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
  // Up to CHUNK_CONCURRENCY chunks at a time instead of one after another.
  // A failed chunk still fails the whole read (Supabase's rows are kept),
  // and no later chunk is sent.
  const out = [];
  for (let i = 0; i < chunks.length; i += CHUNK_CONCURRENCY) {
    // eslint-disable-next-line no-await-in-loop
    const answers = await Promise.all(
      chunks.slice(i, i + CHUNK_CONCURRENCY).map((chunk) => listRowsByIds(schema, table, chunk)),
    );
    answers.forEach((rows) => out.push(...rows));
  }
  return out;
}

/**
 * Overlay one Supabase result ({data, error, ...}) for `table` in `schema`.
 * Never throws; on anything unexpected the result comes back untouched.
 * Exported for tests.
 */
export async function overlayResult(schema, table, result) {
  try {
    if (!result || result.error || result.data === null || result.data === undefined) return result;
    const single = !Array.isArray(result.data);
    const rows = single ? [result.data] : result.data;
    if (!rows.length || typeof rows[0] !== "object") return result;
    const ids = [...new Set(rows.map((r) => r && r.id).filter((v) => v !== undefined && v !== null).map(String))];
    if (!ids.length) return result;
    // No returned column is PHI (e.g. `id, forms_ind`): nothing would be
    // merged, yet RDS would still return -- and audit -- every row in full.
    // So RDS is not asked at all, not even for its flags.
    if (!rows.some((r) => r && typeof r === "object" && Object.keys(r).some(isPhiKey))) return result;

    // eslint-disable-next-line global-require
    const { isCutoverEffective, reportCutoverOutcome, rdsCircuitOpen, tripRdsCircuit } = require("./rdsCutover");
    // The error classes are rdsPhi's: rdsCutover imports them without
    // re-exporting them.
    // eslint-disable-next-line global-require
    const { PhiClientError, PhiClientUnavailable } = require("./rdsPhi");
    const pilot = overlayFlagFor(table);
    if (!(await isCutoverEffective(pilot))) return result;

    const base = { pilot, tenant: schema, table, sample_size: rows.length };
    // The breaker every cutover shares: once RDS timed out or could not be
    // reached, reads skip it for the cooldown instead of each waiting out
    // its own timeout.
    if (rdsCircuitOpen()) {
      reportCutoverOutcome({ ...base, served: "supabase_fallback", outcome: "skipped", reason: "circuit_open" });
      return result;
    }
    let rdsRows;
    try {
      rdsRows = await fetchRdsRows(schema, table, ids);
    } catch (err) {
      if (err instanceof PhiClientError) {
        reportCutoverOutcome({
          ...base, served: "supabase_fallback", reason: `PhiClientError ${err.statusCode}`,
          outcome: err.statusCode === 404 ? "rds_not_mirrored" : "rds_error", status_code: err.statusCode,
          detail: typeof err.detail === "string" ? err.detail : "non-string detail omitted",
        });
      } else if (err instanceof PhiClientUnavailable) {
        const msg = String(err.message || "");
        const reason = msg.includes("timed out") ? "timeout" : msg.includes("no active session") ? "no_session" : "unreachable";
        // Same rule as the other cutovers: a timeout or a failed connection
        // trips the breaker; a missing session is not RDS being down.
        if (reason === "timeout" || reason === "unreachable") tripRdsCircuit();
        reportCutoverOutcome({ ...base, served: "supabase_fallback", outcome: "rds_unavailable", reason });
      } else {
        reportCutoverOutcome({ ...base, served: "supabase_fallback", outcome: "unexpected_error", reason: err?.name || "Error" });
      }
      return result;
    }
    const merged = mergePhi(rows, rdsRows);
    reportCutoverOutcome({
      ...base, served: "rds", outcome: merged.missing === rows.length ? "rds_empty" : "ok",
      matched: merged.matched, mismatched: merged.mismatched, missing: merged.missing,
      masked: merged.masked, columns: merged.columns,
    });
    return { ...result, data: single ? merged.rows[0] : merged.rows };
  } catch (_) {
    return result;
  }
}

// ---------------------------------------------------------------------------
// The write overlay (plan §31; see the module comment for the rules).
// ---------------------------------------------------------------------------

// Keys whose change RDS must see even though they are not PHI: they decide
// WHO may read the row (rds_access.resolve). A payload touching none of
// these and no PHI column changes nothing RDS holds.
const OWNER_KEYS = new Set(["user_id", "shared_users", "created_by", "owner_id"]);
const FORWARD_CONCURRENCY = 10;
// Retry ladder for a failed forward; after the last rung the write is lost.
const RETRY_DELAYS_MS = [1000, 5000, 30000];
const RETRY_MAX = 200;
const WARN_INTERVAL_MS = 60000;
// Ids of a Supabase re-read travel in the URL (`.in("id", ...)`): 100 keeps
// each request well inside the gateway's line limit.
const REFETCH_CHUNK = 100;

/** The dashboard pilot a table's write forwards report under. */
export function writePilotFor(table) {
  return `write_${overlayFlagFor(table)}`;
}

function deps() {
  // eslint-disable-next-line global-require
  const phi = require("./rdsPhi");
  // eslint-disable-next-line global-require
  const cutover = require("./rdsCutover");
  return { ...phi, ...cutover };
}

/**
 * Should writes to `table` be forwarded to RDS right now? True when the
 * table's overlay flag is effective OR RDS is independent -- the sticky
 * source mode, so a flag lookup that fails while independent still
 * forwards (data #3: RDS being THE read path, a silent skip would be a
 * lost write, not a safe default). Never throws.
 */
export async function writeOverlayActive(table) {
  if (!PHI_TABLES.has(table)) return false;
  let d;
  try {
    d = deps();
  } catch (_) {
    return false;
  }
  try {
    if (await d.isCutoverEffective(overlayFlagFor(table))) return true;
  } catch (_) {
    // fall through to the source mode
  }
  try {
    return typeof d.isIndependent === "function" && (await d.isIndependent()) === true;
  } catch (_) {
    return false;
  }
}

function hasId(row) {
  return row && typeof row === "object" && row.id !== undefined && row.id !== null;
}

function rowsOf(data) {
  if (Array.isArray(data)) return data.filter(hasId);
  return hasId(data) ? [data] : [];
}

/** Does an update payload touch anything RDS holds (PHI or ownership)? */
function payloadTouchesRds(payload) {
  const rows = Array.isArray(payload) ? payload : [payload];
  return rows.some((r) => r && typeof r === "object"
    && Object.keys(r).some((k) => isPhiKey(k) || OWNER_KEYS.has(String(k).toLowerCase())));
}

/** Record the `id` filters a mutating chain applies (only the id column:
 * anything else needs Supabase to tell us which rows it touched). */
function noteIdFilter(state, key, args) {
  if (key === "eq" && args[0] === "id" && args[1] !== undefined && args[1] !== null) {
    state.ids.push(args[1]);
  } else if (key === "in" && args[0] === "id" && Array.isArray(args[1])) {
    args[1].forEach((v) => { if (v !== undefined && v !== null) state.ids.push(v); });
  } else if (key === "match" && hasId(args[0])) {
    state.ids.push(args[0].id);
  }
}

/** Inserts always need Supabase's rows (server-side ids / defaults);
 * updates, deletes and upserts only when the chain named no id. */
function needsReturnedRows(op, ids) {
  return op === "insert" || ids.length === 0;
}

/** Chain `select()` on the raw builder so the mutation RETURNS its rows.
 * postgrest-js mutates and returns the same builder; a builder without
 * `select` (or one that throws) means we go without ids. */
function autoSelect(b) {
  try {
    if (typeof b.select !== "function") return { source: b, autoSelected: false };
    const next = b.select();
    return { source: next && typeof next.then === "function" ? next : b, autoSelected: true };
  } catch (_) {
    return { source: b, autoSelected: false };
  }
}

/** The caller asked for no representation: give back the shape a chain
 * without select() returns (data null), status as answered (data #15). */
function restoreShape(result, autoSelected) {
  if (!autoSelected || !result || typeof result !== "object") return result;
  return { ...result, data: null };
}

/**
 * Decide what to forward for one statement. Pure; exported for tests.
 * Returns { skip } (a supabase_only reason) or { items: [{op, row, id}] }.
 */
export function planForwards(op, payload, knownIds, returnedRows) {
  const payloadRows = Array.isArray(payload) ? payload : [payload];
  const one = (row, fop) => ({ op: fop, row, id: String(row.id) });
  if (op === "insert" || op === "upsert") {
    let rows = returnedRows;
    if (!rows.length) {
      // An upsert keyed by the chain and not selected: the payload IS the
      // full row; a lone payload without its id takes the chain's one.
      rows = payloadRows.filter((r) => r && typeof r === "object")
        .map((r) => (!hasId(r) && payloadRows.length === 1 && knownIds.length === 1 ? { ...r, id: knownIds[0] } : r))
        .filter(hasId);
    }
    return rows.length ? { items: rows.map((r) => one(r, "upsert")) } : { skip: "no_id" };
  }
  if (op === "update") {
    if (!payloadTouchesRds(payload)) return { skip: "no_phi_columns" };
    const ids = knownIds.length ? knownIds : returnedRows.map((r) => r.id);
    if (!ids.length) return { skip: "no_id" };
    const changes = payloadRows.find((r) => r && typeof r === "object") || {};
    return { items: [...new Set(ids.map(String))].map((id) => one({ ...changes, id }, "update")) };
  }
  if (op === "delete") {
    const rows = returnedRows.length ? returnedRows : knownIds.map((id) => ({ id }));
    return rows.length ? { items: rows.map((r) => one(r, "delete")) } : { skip: "no_id" };
  }
  return { skip: "no_id" };
}

/** Classify a failed forward the way the read overlay does, plus whether
 * a retry could help. Never carries row content. */
function classifyFailure(err, d) {
  if (err instanceof d.PhiClientError) {
    const detail = typeof err.detail === "string" ? err.detail : "";
    if (err.statusCode === 403) {
      return { ok: false, retry: false, outcome: "rds_forbidden", reason: "PhiClientError 403", status_code: 403 };
    }
    if (err.statusCode === 409 && /no primary key/i.test(detail)) {
      return { ok: false, retry: false, outcome: "no_primary_key", reason: "no_primary_key", status_code: 409 };
    }
    return { ok: false, retry: true, outcome: "rds_error", reason: `PhiClientError ${err.statusCode}`, status_code: err.statusCode };
  }
  if (err instanceof d.PhiClientUnavailable) {
    const msg = String(err.message || "");
    const reason = msg.includes("timed out") ? "timeout" : msg.includes("no active session") ? "no_session" : "unreachable";
    return { ok: false, retry: true, outcome: "rds_unavailable", reason };
  }
  return { ok: false, retry: true, outcome: "unexpected_error", reason: err?.name || "Error" };
}

/** Re-read one full row from Supabase through the RAW client (no overlay:
 * the point is Supabase's own copy). Null when it cannot be read. */
async function fetchSupabaseRow(client, table, id) {
  try {
    const { data, error } = await client.from(table).select("*").eq("id", id);
    if (error) return null;
    const rows = rowsOf(data);
    return rows.find((r) => String(r.id) === String(id)) || null;
  } catch (_) {
    return null;
  }
}

/** Re-read full rows by id through the RAW client (see fetchSupabaseRow),
 * keyed by String(id). A chunk that cannot be read contributes no rows:
 * the caller falls back per row. */
async function fetchSupabaseRows(client, table, ids) {
  const out = new Map();
  for (let i = 0; i < ids.length; i += REFETCH_CHUNK) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data, error } = await client.from(table).select("*").in("id", ids.slice(i, i + REFETCH_CHUNK));
      if (!error) rowsOf(data).forEach((r) => out.set(String(r.id), r));
    } catch (_) {
      // unreadable chunk: its rows fall back to payload + returned row
    }
  }
  return out;
}

/** Does a select() argument return every column? postgrest-js defaults to
 * "*"; otherwise a top-level "*" token is needed -- one inside an embedded
 * resource (`other(*)`) does not count. */
function selectsEveryColumn(columns) {
  if (columns === undefined || columns === null) return true;
  if (typeof columns !== "string") return false;
  let flat = columns.replace(/\s+/g, "");
  let stripped = flat.replace(/\([^()]*\)/g, "");
  while (stripped !== flat) {
    flat = stripped;
    stripped = flat.replace(/\([^()]*\)/g, "");
  }
  return flat.split(",").includes("*");
}

/** The payload row a returned row came from: by id when the payload named
 * one, else by position (server-generated ids come back in order). */
function payloadRowFor(payloadRows, returned, index) {
  const byId = payloadRows.find((p) => hasId(p) && String(p.id) === String(returned.id));
  if (byId) return byId;
  const atIndex = payloadRows[index];
  return atIndex && !hasId(atIndex) ? atIndex : {};
}

/**
 * Inserts/upserts forward the rows Supabase RETURNED as FULL upserts, so
 * they must be whole: a caller-chained column-limited select
 * (`.select("id, patient_name")`) returns partial rows, and upserting one
 * would overwrite the row's other PHI columns with NULL in RDS. When the
 * select was not "*"-equivalent, or a returned row lacks a key the
 * payload carried, the full rows are re-read through the RAW client (as
 * the missing->refetch path does); a row the re-read cannot supply falls
 * back to the payload row merged with the returned one.
 */
async function completeReturnedRows(client, table, state, returned) {
  if (!returned.length || (state.op !== "insert" && state.op !== "upsert")) return returned;
  const payloadRows = (Array.isArray(state.payload) ? state.payload : [state.payload])
    .filter((r) => r && typeof r === "object");
  const wanted = new Set();
  payloadRows.forEach((r) => Object.keys(r).forEach((k) => wanted.add(k)));
  const keys = Array.from(wanted);
  const partial = (state.selected && !selectsEveryColumn(state.selectColumns))
    || returned.some((r) => keys.some((k) => !Object.prototype.hasOwnProperty.call(r, k)));
  if (!partial) return returned;
  const full = await fetchSupabaseRows(client, table, returned.map((r) => r.id));
  return returned.map((r, i) => full.get(String(r.id)) || { ...payloadRowFor(payloadRows, r, i), ...r });
}

/** Forward one row. Resolves to {ok:true} or a classifyFailure() result;
 * never rejects. */
async function forwardOne(d, client, schema, table, item) {
  try {
    if (item.op === "delete") {
      try {
        await d.deleteRowByBody(schema, table, item.row);
      } catch (err) {
        // RDS never had (or already lost) the row: the delete is a no-op,
        // and no retry could make it more deleted.
        if (!(err instanceof d.PhiClientError && err.statusCode === 404)) throw err;
      }
      return { ok: true };
    }
    const res = await d.writeRow(schema, table, item.row, { op: item.op });
    if (item.op === "update" && res && res.written === false && res.reason === "missing") {
      // RDS lacks the row: a partial row would later overlay NULL PHI, so
      // the whole row comes from Supabase and is upserted (data #2).
      const full = await fetchSupabaseRow(client, table, item.id);
      if (!full) {
        // Supabase could not answer the re-read (transient): retried like
        // any other failure and, when the ladder runs out, audited as a
        // lost write rather than vanishing without a trace.
        return { ok: false, retry: true, outcome: "unexpected_error", reason: "refetch_failed" };
      }
      await d.writeRow(schema, table, full, { op: "upsert" });
    }
    return { ok: true };
  } catch (err) {
    return classifyFailure(err, d);
  }
}

// -- per-row ordering and the retry queue ------------------------------------

// (schema, table, id) -> the promise of the last forward for that row.
const rowChains = new Map();

function retryKey(schema, table, id) {
  return `${schema}\u0000${table}\u0000${String(id)}`;
}

/** Run `fn` after every earlier forward for the same row has settled. */
function serialized(key, fn) {
  const prev = rowChains.get(key) || Promise.resolve();
  const next = prev.then(fn, fn);
  rowChains.set(key, next);
  next.then(() => { if (rowChains.get(key) === next) rowChains.delete(key); }, () => {});
  return next;
}

/** Map `items` through `fn` with at most `limit` in flight; `fn` never
 * rejects, so the results are its resolved values in order. */
async function pool(items, limit, fn) {
  const results = new Array(items.length);
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const i = cursor;
      cursor += 1;
      // eslint-disable-next-line no-await-in-loop
      results[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// key -> {schema, table, op, row, viewer, attempts, key, client, timer, failure}
const retryQueue = new Map();
const lastWarnAt = new Map();

/** console.warn at most once per table per minute, table + count only. */
function warnRateLimited(table, count, what) {
  const now = Date.now();
  if (now - (lastWarnAt.get(table) || 0) < WARN_INTERVAL_MS) return;
  lastWarnAt.set(table, now);
  // eslint-disable-next-line no-console
  console.warn(`[rdsPhiOverlay] RDS write forward ${what}: table ${table}, ${count} row(s)`);
}

/** The signed-in user's id (JWT sub) from the client's own auth, falling
 * back to the app client for a schema-scoped builder without one. */
async function sessionSub(client) {
  try {
    let auth = client?.auth;
    if (!auth || typeof auth.getSession !== "function") {
      // eslint-disable-next-line global-require
      auth = (await require("../components/supabaseClient").getSupabase())?.auth;
    }
    const { data: { session } = {} } = await auth.getSession();
    return session?.user?.id || null;
  } catch (_) {
    return null;
  }
}

function reportLost(d, entry, reason, extra) {
  d.reportCutoverOutcome({
    pilot: writePilotFor(entry.table), tenant: entry.schema, table: entry.table, op: entry.op,
    sample_size: 1, served: "supabase_fallback", outcome: "rds_write_lost", reason, missing: 1, ...extra,
  });
}

/** The last retry failed (or none is possible): the loss goes to the
 * dashboard and, durably, to RDS's audit log (clinical #11). */
function loseEntry(d, entry, failure) {
  retryQueue.delete(entry.key);
  const extra = failure.status_code ? { status_code: failure.status_code } : {};
  reportLost(d, entry, failure.reason, extra);
  try {
    if (typeof d.reportWriteMiss === "function") {
      Promise.resolve(d.reportWriteMiss(entry.schema, entry.table, entry.row.id, String(failure.reason).slice(0, 120)))
        .catch(() => {});
    }
  } catch (_) {
    // reportWriteMiss never throws; belt and braces.
  }
  warnRateLimited(entry.table, 1, "lost");
}

function clearTimer(entry) {
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = null;
}

/** A newer forward for the same row landed CARRYING the queued change (a
 * merged update): the entry is moot. */
function dropQueued(key) {
  const entry = retryQueue.get(key);
  if (!entry) return;
  clearTimer(entry);
  retryQueue.delete(key);
}

/** A newer forward of ANOTHER op for the same row landed without this
 * entry's change (an update after a queued upsert, say): the change cannot
 * ride along, so it is lost -- reported and audited like a final retry
 * failure, never dropped silently. */
function supersedeQueued(d, entry) {
  clearTimer(entry);
  loseEntry(d, entry, { reason: "superseded" });
}

/** Another user is signed in now: their session must not carry the
 * previous user's write, and the row is theirs to re-edit. */
function dropForViewerChange(d, entry) {
  clearTimer(entry);
  retryQueue.delete(entry.key);
  reportLost(d, entry, "viewer_changed");
  warnRateLimited(entry.table, 1, "dropped (viewer changed)");
}

/** Can `item`, about to be forwarded by `viewer`, carry a queued entry's
 * change? Only an update over a queued update of the SAME viewer: their
 * columns merge (queued first, newer wins). Anything else cannot be
 * folded into one statement, and another user's session must never carry
 * a previous user's write. */
function canMerge(queued, item, viewer) {
  return Boolean(queued) && queued.op === "update" && item.op === "update" && queued.viewer === viewer;
}

function schedule(entry) {
  clearTimer(entry);
  entry.timer = setTimeout(() => { entry.timer = null; retryEntry(entry); }, RETRY_DELAYS_MS[entry.attempts]);
  if (entry.timer && typeof entry.timer.unref === "function") entry.timer.unref();
}

async function retryEntry(entry) {
  if (retryQueue.get(entry.key) !== entry) return; // superseded meanwhile
  const d = deps();
  // RDS paused (supabase_only): nothing reaches RDS any more, queued
  // retries included. The row is in Supabase; a later return to
  // independent catches it up.
  if (typeof d.getSourceMode === "function" && (await d.getSourceMode()) === "supabase_only") {
    clearTimer(entry);
    if (retryQueue.get(entry.key) === entry) retryQueue.delete(entry.key);
    return;
  }
  const viewer = await sessionSub(entry.client);
  if (viewer !== entry.viewer) {
    dropForViewerChange(d, entry);
    return;
  }
  // Re-checked once the row's chain reaches this retry: a newer statement
  // that landed meanwhile carried (or superseded) this entry, and a stale
  // re-forward behind it would undo "newer wins".
  const res = await serialized(entry.key, () => (retryQueue.get(entry.key) === entry
    ? forwardOne(d, entry.client, entry.schema, entry.table, entry)
    : null));
  if (!res || retryQueue.get(entry.key) !== entry) return;
  if (res.ok) { retryQueue.delete(entry.key); return; }
  entry.attempts += 1;
  entry.failure = res;
  if (!res.retry || entry.attempts >= RETRY_DELAYS_MS.length) { loseEntry(d, entry, res); return; }
  schedule(entry);
}

function enqueueRetry(d, client, schema, table, item, viewer, failure) {
  const key = retryKey(schema, table, item.id);
  const existing = retryQueue.get(key);
  let { row } = item;
  if (existing) {
    clearTimer(existing);
    if (canMerge(existing, item, viewer)) row = { ...existing.row, ...item.row }; // queued first, newer wins
    else if (existing.viewer !== viewer) dropForViewerChange(d, existing);
    else supersedeQueued(d, existing);
  }
  const entry = {
    schema, table, op: item.op, row, id: item.id, viewer, attempts: 0, key, client, timer: null, failure,
  };
  if (!existing && retryQueue.size >= RETRY_MAX) {
    // A queue this deep means RDS has been gone a while; keeping more in
    // memory only delays the audit record.
    loseEntry(d, entry, { ...failure, reason: "retry_queue_full" });
    return;
  }
  retryQueue.set(key, entry);
  schedule(entry);
}

/** Test hook -- run every queued retry now (a failing one is re-queued
 * or lost exactly as a timer-driven retry would be). */
export async function _flushWriteRetries() {
  const entries = Array.from(retryQueue.values());
  entries.forEach(clearTimer);
  await Promise.all(entries.map((e) => retryEntry(e)));
}

/** Test hook -- forget every queued retry, timer and warn timestamp. */
export function _resetWriteRetries() {
  retryQueue.forEach(clearTimer);
  retryQueue.clear();
  rowChains.clear();
  lastWarnAt.clear();
}

/** Test hook / diagnostics -- how many rows await a retry. */
export function _writeRetryQueueSize() {
  return retryQueue.size;
}

// -- the statement -----------------------------------------------------------

/** Forward every item (serialized per row, bounded fan-out), queue the
 * retriable failures, and return the per-item results. */
async function forwardAll(d, client, schema, table, items) {
  let viewer;
  const currentViewer = async () => {
    if (viewer === undefined) viewer = await sessionSub(client);
    return viewer;
  };
  // A queued update of a row about to be updated again rides along: its
  // columns merge into the newer item (queued first, newer wins), so a
  // partial update that touched OTHER columns is never discarded.
  const carried = new Map();
  for (let i = 0; i < items.length; i += 1) {
    const key = retryKey(schema, table, items[i].id);
    const queued = retryQueue.get(key);
    // eslint-disable-next-line no-await-in-loop
    if (queued && canMerge(queued, items[i], await currentViewer())) {
      items[i] = { ...items[i], row: { ...queued.row, ...items[i].row } };
      carried.set(key, queued);
    }
  }
  const results = await pool(items, FORWARD_CONCURRENCY, (item) => serialized(
    retryKey(schema, table, item.id),
    () => forwardOne(d, client, schema, table, item),
  ));
  for (let i = 0; i < items.length; i += 1) {
    const key = retryKey(schema, table, items[i].id);
    if (results[i].ok) {
      const queued = retryQueue.get(key);
      if (queued) {
        // eslint-disable-next-line no-await-in-loop
        const who = await currentViewer();
        if (queued === carried.get(key)) dropQueued(key); // its columns landed with this forward
        else if (queued.viewer !== who) dropForViewerChange(d, queued);
        else if (!canMerge(queued, items[i], who)) supersedeQueued(d, queued); // another op: lost, not silent
        // else: a same-viewer update queued after the merge point -- its own retry lands it
      }
    } else if (results[i].retry) {
      // eslint-disable-next-line no-await-in-loop
      enqueueRetry(d, client, schema, table, items[i], await currentViewer(), results[i]);
    }
  }
  return results;
}

/** One dashboard report per statement: counts and error classes only. */
function reportStatement(d, base, results) {
  const failed = results.filter((r) => !r.ok);
  if (!failed.length) {
    d.reportCutoverOutcome({ ...base, served: "rds", outcome: "ok" });
    return;
  }
  const first = failed[0];
  d.reportCutoverOutcome({
    ...base, served: "supabase_fallback", outcome: first.outcome, reason: first.reason, missing: failed.length,
    ...(first.status_code ? { status_code: first.status_code } : {}),
  });
  const queued = failed.filter((r) => r.retry).length;
  if (queued) warnRateLimited(base.table, queued, "queued for retry");
}

/**
 * Settle a mutating chain: Supabase first (exactly today's request unless
 * an auto-select was needed), then the forward to RDS, awaited. Resolves
 * to what the caller would have seen (see restoreShape). Never rejects
 * for a reason of its own: only Supabase's own rejection propagates.
 */
async function settleMutation(b, client, schema, table, state) {
  const active = await writeOverlayActive(table);
  if (!active) return b; // adopted: byte-for-byte today's behaviour
  const { source, autoSelected } = !state.selected && needsReturnedRows(state.op, state.ids)
    ? autoSelect(b) : { source: b, autoSelected: false };
  const result = await source;
  try {
    if (!result || result.error) return restoreShape(result, autoSelected);
    const d = deps();
    const returned = await completeReturnedRows(client, table, state, rowsOf(result.data));
    const plan = planForwards(state.op, state.payload, state.ids, returned);
    const base = { pilot: writePilotFor(table), tenant: schema, table, op: state.op, sample_size: plan.items ? plan.items.length : 0 };
    if (plan.skip) {
      d.reportCutoverOutcome({ ...base, served: "supabase_only", reason: plan.skip });
      return restoreShape(result, autoSelected);
    }
    const results = await forwardAll(d, client, schema, table, plan.items);
    reportStatement(d, base, results);
  } catch (_) {
    // The forward must never turn a succeeded Supabase write into an error.
  }
  return restoreShape(result, autoSelected);
}

// The explicit name wins: the caller knows which tenant it built the client
// for (and `.schema(name)` re-wraps pass theirs); the client's own
// schemaName is the fallback for a client handed in without one.
function schemaOf(client, explicit) {
  return explicit || client?.rest?.schemaName || client?.schemaName || "public";
}

function wrapBuilder(builder, client, schema, table, state) {
  return new Proxy(builder, {
    get(b, key) {
      if (key === "then") {
        return (onFulfilled, onRejected) => (state.mutating
          ? settleMutation(b, client, schema, table, state)
          : b.then((result) => overlayResult(schema, table, result))
        ).then(onFulfilled, onRejected);
      }
      const value = Reflect.get(b, key);
      if (typeof value !== "function") return value;
      return (...args) => {
        if (MUTATING.has(key)) {
          state.mutating = true;
          state.op = key;
          state.payload = args[0];
        } else if (state.mutating) {
          if (key === "select") {
            state.selected = true;
            state.selectColumns = args[0]; // undefined = postgrest-js's "*"
          }
          noteIdFilter(state, key, args);
        }
        const next = value.apply(b, args);
        return next && typeof next === "object" && typeof next.then === "function"
          ? wrapBuilder(next, client, schema, table, state)
          : next;
      };
    },
  });
}

/**
 * Wrap a Supabase client so every read of a PHI table gets RDS's PHI
 * columns overlaid and every write is forwarded (see the module comment).
 * `schemaName` is the schema the client was created for; `.schema(name)`
 * calls are re-wrapped with their own name. Returns the client itself
 * when the build switch is off.
 */
export function wrapSupabaseClientForPhiOverlay(client, schemaName) {
  if (!client) return client;
  // eslint-disable-next-line global-require
  const { cutoverBuildEnabled } = require("./rdsCutover");
  if (!cutoverBuildEnabled()) return client;
  const schema = schemaOf(client, schemaName);
  return new Proxy(client, {
    get(target, prop) {
      if (prop === "schema") {
        return (name) => wrapSupabaseClientForPhiOverlay(target.schema(name), name);
      }
      if (prop === "from") {
        return (table) => {
          const builder = target.from(table);
          return PHI_TABLES.has(table)
            ? wrapBuilder(builder, target, schema, table, {
              mutating: false, op: null, payload: undefined, ids: [], selected: false, selectColumns: undefined,
            })
            : builder;
        };
      }
      return Reflect.get(target, prop);
    },
  });
}
